import React, { useEffect, useMemo, useRef, useState } from 'react';

function matches(name, query) {
  return String(name || '')
    .toLowerCase()
    .includes(String(query || '').trim().toLowerCase());
}

function StrainPicker({
  id,
  strains,
  value,
  onSelect,
  allowOther = true,
  placeholder = 'Type to find a strain',
  emptyLabel = 'No matching strains',
}) {
  const [query, setQuery] = useState(value && value !== 'Other' ? value : '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value && value !== 'Other') {
      setQuery(value);
    }
    if (value === 'Other') {
      setQuery('Other');
    }
    if (!value) {
      setQuery('');
    }
  }, [value]);

  const filtered = useMemo(() => {
    if (!query.trim() || query === 'Other') return strains;
    return strains.filter((strain) => matches(strain.name, query));
  }, [strains, query]);

  const items = useMemo(() => {
    const names = filtered.map((strain) => strain.name);
    return allowOther ? [...names, 'Other'] : names;
  }, [filtered, allowOther]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const choose = (name) => {
    if (name === 'Other') {
      const typed = query.trim();
      onSelect('Other', typed && typed.toLowerCase() !== 'other' ? typed : '');
    } else {
      onSelect(name);
    }
    setQuery(name);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.min(current + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      if (items[highlight]) choose(items[highlight]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="strain-picker" ref={wrapRef}>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          setHighlight(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          if (value) onSelect('');
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          window.setTimeout(() => {
            const typed = query.trim().toLowerCase();
            if (!typed) return;
            const exact = strains.find((strain) => strain.name.toLowerCase() === typed);
            if (exact) {
              onSelect(exact.name);
              setQuery(exact.name);
              setOpen(false);
            }
          }, 120);
        }}
      />
      {open && (
        <ul id={`${id}-list`} className="strain-menu" role="listbox">
          {filtered.length === 0 && (
            <li className="strain-empty">{emptyLabel}</li>
          )}
          {items.map((name, index) => (
            <li key={name}>
              <button
                type="button"
                role="option"
                className={
                  `strain-option${name === 'Other' ? ' other' : ''}${index === highlight ? ' highlighted' : ''}`
                }
                aria-selected={value === name}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(name)}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default StrainPicker;
